import React, { Fragment, useRef } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import { 
    setProcessing, setPanes, setFormServerErrors, setFormValue,
    loadPage, loadEntity, createEntity, saveEntity, activateDeactivateEntity, removeEntityFromUI, deleteEntity,
    disposePage, 
    setPageValue
} from "../../redux/slices/userspageslice";
import { setCurentPage, setCurrentPageIsDirty, setCurrentPage } from "../../redux/slices/currentpageslice";
import { addNotification } from "../../redux/slices/notificationsslice";
import { hideAppDialog, setAppDialog } from "../../redux/slices/appdialogslice";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Page } from "../page";
import { UsersPageBody } from "./userspagebody";
import { formValidator } from "./userspageformvalidator";
import { deepCompare, notificationDurationMiddle } from "../../lib/utils";

import "./userspage.css"; 
import { usersMaxReached } from "../../routing/authorizationhandlers";

import i18next from "i18next";
import { useTranslation } from "react-i18next";


export const UsersPage = props => {
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.currentUser);
    const currentTheme = useSelector(state => state.currentTheme);
    const pageState = useSelector(state => state.usersPage);
    const currentPageState = useSelector(state => state.currentPage);
    const navigate = useNavigate();

    const { t } = useTranslation();

    let currentPageId = "usersPage";

    /*block navigation if dirty*/
    const updateDirtyState = () => {
        //exclude groupsids because when i remove then add elements the position in the array changes
        let {GroupsIds, ...fiv} = pageState.formInitialValues;
        let {GroupsIds: _GroupsIds, ...fv} = pageState.formValues;

        //let isDirty = !deepCompare(pageState.formInitialValues, pageState.formValues);
        let isDirty = !deepCompare(fiv, fv);
        let wasDirty = currentPageState.isDirty;

        if(isDirty != wasDirty)
            dispatch(setCurrentPageIsDirty(isDirty));
    };

    const blockNavigationIfDirty = (msg) => {
        if (currentPageState.isDirty)
        {
            if (window.confirm(msg ? msg : t("users.unsaved_changes")) == true)
                {
                    dispatch(setCurrentPageIsDirty(false));
                    return false;
                }
            else
                return true;
        }
        else
            return false;
    };
    /*block navigation if dirty*/

    /*initial page load*/
    useEffectOnce(() => {
        dispatch(loadPage(t)).then(
            response => {
                dispatch(setCurrentPage({id: currentPageId, isDirty: false}));
            },
            err =>{
                //hide loader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) =>{
                    let errorMessages = payload?.data?.ErrorMessages;

                    //notify with error
                    if (!errorMessages || errorMessages.length == 0)
                        dispatch(addNotification({text: t("users.loading_errormessage"), type: "error", closable: true, duration:3, position:"topRight"}));
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);
                        dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:3, position:"topRight"}));
                    }

                    //go home
                    navigate("/", {replace: true});
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    dispatch(addNotification({text: t("users.unknown_error"), type:"error", closable: true, duration: 3, position: "topRight"}));
                    navigate("/", {replace: true});
                };

                //console.log(action);
                apiErrorsHandler({}, dispatch, navigate, err, "/", {responseReceived: handleResponseReceived, unhandled: handleUnhandled});
            }
        );

        return () => {
            dispatch(setCurrentPage({id: "", isDirty: false}));
            dispatch(disposePage(true));
        }
    }, []);
    /*initial load*/


    /*splitter*/
    const handleOnSplitterChange = e => {
        dispatch(setPanes(e.newState));
    };
    /*splitter*/


    /*toolbar*/
    const handleOnNew = e => {
        if (!currentUser.IsAuthenticated || usersMaxReached(currentUser))
            return;

        if (blockNavigationIfDirty() == true)
            return;

        dispatch(createEntity());
    };

    const handleOnSave = e =>{
        if (pageState.mode == 1)
        {
            if (!currentUser.IsAuthenticated || usersMaxReached(currentUser))
                return;
        }
        
        //make sure no client errors; if so return
        const isValidForm = formValidator(pageState.mode)(pageState.formValues) === undefined;
        if (!isValidForm)
        {
            dispatch(addNotification({text: t("users.invalid_info"), type: "error", closable: true, duration:3, position:"topRight"}));
            return;
        }

        //no client errors => proceed
        dispatch(saveEntity(t)).then(
            response =>{
                dispatch(setCurrentPageIsDirty(false));
            },
            err =>
            { 
                //hide loader
                dispatch(setProcessing({processing: false}));

                dispatch(addNotification({text: err.data?.Errors[0]?.Code , type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = [t("users.unknown_error")];
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
                        if (payload.data.ErrorCodes.EntityDoesNotExist)
                        {
                            dispatch(removeEntityFromUI(pageState.formValues.Id));
                            dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:3, position:"topRight"}));
                        }
                        else
                            dispatch(setFormServerErrors(errorMessages));
                    }
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("users.unknown_error");
                    dispatch(setFormServerErrors([errorMessage]));                
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );
    };

    const handleOnActivateDeactivate = e =>{
        if (pageState.mode == 1)
            return;
            
        const activate = !pageState.formValues.IsActive;

        //no client errors => proceed
        dispatch(activateDeactivateEntity(activate, t)).then(
            response =>{},
            err =>
            {
                //hide loader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = [t("users.unknown_error")];
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        //if the error is entity does not exist => it has been removed => remove it from entities
                        if (payload.data.ErrorCodes.EntityDoesNotExist)
                        {
                            dispatch(removeEntityFromUI(pageState.formValues.Id));
                            dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:3, position:"topRight"}));
                        }
                        else
                            dispatch(setFormServerErrors(errorMessages));
                    }
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("users.unknown_error");
                    dispatch(setFormServerErrors([errorMessage]));                
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );
    };

    /*on group checked/unchecked*/
    const handleOnGroupCheck = (item) => {
        let _groups = pageState.formValues.Groups.map(e => {
            if (e.Id == item.Id)
                return {...e, Checked: !e.Checked};
            else
                return {...e};
        });

        let groupsIds = [];
        let id = item.Id;
        let isChecked = !item.Checked;

        if (isChecked)
            groupsIds = [...pageState.formValues.GroupsIds, id];
        else
            groupsIds = pageState.formValues.GroupsIds.filter(e => e != id);


        dispatch(setFormValue({id: "Groups", value: _groups}));
        dispatch(setFormValue({id: "GroupsIds", value: groupsIds}));
    };

    /*on user checked/unchecked*/
    const handleOnErrorbarClose = e => {
        dispatch(setFormServerErrors([]));
    };

    /*toolbar*/

    /*delete entity*/
    const handleOnDelete = (id) => {
        //id is zero if deleted from toolbar and > 0 if deleted from entities list
        //if id is zero and mode !=2 => must not happen
        if (!id && pageState.mode != 2)
            return;        

        let isCurrentEntity = false;

        if (!id)//deleted from the toolbar => it's the current entity and mode is 2
        {
            id = pageState.formValues.Id;
            isCurrentEntity = true;
        }
        else if (pageState.formValues?.Id && pageState.formValues.Id == id)
            isCurrentEntity = true;


        if (isCurrentEntity)
        {
            //if contains dirty => block
            if (blockNavigationIfDirty() == true)
                return;
        }

        dispatch(setAppDialog({
            isVisible: true,
            className: "users-page-are-you-sure",
            title: t("users.warning"), content: t("users.areyousure"),
            actions:{
                yesButton:{ title: t("users.yes"), handler: () =>{
                    dispatch(hideAppDialog());

                    dispatch(deleteEntity(id, t))
                    .then(
                        response =>{
                            dispatch(setProcessing({processing:""}));
                        },
                        err => {
                            dispatch(setProcessing({processing:""}));

                            const handleResponseReceived = (state, dispatch, navigate, payload) => {
                                let errorMessages = payload?.data?.ErrorMessages;
            
                                if (!errorMessages || errorMessages.length == 0)
                                    errorMessages = [t("users.unknown_error")];
                                else
                                {
                                    errorMessages = getErrorMessages(errorMessages, 1);
            
                                    if (payload.data.ErrorCodes.EntityDoesNotExist)
                                        dispatch(removeEntityFromUI(id));                        
                                }
            
                                dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:3, position:"topRight"}));
                            };
            
                            const handleUnhandled = (state, dispatch, navigate, payload) =>{
                                let errorMessage = t("users.unknown_error");
                                dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration:3, position:"topRight"}));
                            };
            
                            apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
                        });
                }},
                noButton: {title: t("users.no")}
            }
        }));
    };
    /*delete entity*/

    /*view/edit entity*/
    const handleOnEntitySelected = (entityId, item) => {
        if (blockNavigationIfDirty() == true)
            return;

        dispatch(loadEntity(entityId, t))
        .then(
            response =>{},
            err =>
            {
                //hid eloader
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = [t("users.unknown_error")];
                    else
                    {
                        errorMessages = getErrorMessages(errorMessages, 1);

                        if (payload.data.ErrorCodes.EntityDoesNotExist)
                            dispatch(removeEntityFromUI(entityId));                        
                    }

                    dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:3, position:"topRight"}));
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("users.unknown_error");
                    dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration:3, position:"topRight"}));
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );
    };
    /*view/edit entity*/

    //tools visible/hidden
    const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
        //hide
        dispatch(
        setPanes( [                    
            {
                size:"0%",
                min: "%",
                max: "%",
                collapsible: false
            },           
            {
                min: "60%",
                max: "80%",
                collapsible: false,
            },
            {
                size:"20%",
                min: "20%",
                max: "20%",
                collapsible: false,
            }
            ])
        );
    else
        dispatch(
        setPanes( [                    
            {
                size:"20%",
                collapsible: false
            },           
            {
                min: "60%",
                max: "60%",
                collapsible: false,
            },
            {
                size:"20%",
                min: "20%",
                max: "20%",
                collapsible: false,
            }
        ])
        );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
    };

    const handleOnMobileClose = () => {
        dispatch(setPageValue({ id: "mode", value: 0 }));
    };

    return (
        <Fragment>
            <Page id="usersPage" className="users-page" title={t("users.users")} animation={props.animation}>
                <UsersPageBody 
                    onSplitterChange={handleOnSplitterChange} onToolsToggle={handleOnToolsToggle}
                    onNew={handleOnNew} onSave={handleOnSave} onActivateDeactivate={handleOnActivateDeactivate} onDelete={handleOnDelete}
                    onErrorbarClose={handleOnErrorbarClose} onGroupCheck={handleOnGroupCheck}
                    onEntitySelected={handleOnEntitySelected} onEntityDoubleClick={handleOnEntitySelected}
                    onMobileClose={handleOnMobileClose}
                    updateDirtyState={updateDirtyState}
                />
            </Page>
            <style>
                {
                    `
                    /*page class*/
                    .users-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .users-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .users-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .users-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .users-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `
                }
            </style>
        </Fragment>
    );
};

//export const UsersPage = {Component: Users};
